import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, AutoComplete, Button, Space, Empty, Spin, Divider, Row, Col, Typography, Flex, Modal, Input, Select, Tabs, message, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, DatabaseOutlined, SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { getknowledgeDatabases, addKnowledgeDatabase, createOrUpdateQAEntry, createOrUpdateBusinessInfoEntry, deleteEntry } from '../../../KnowledgeDatabase/KnowledgeDatabaseServices';
import QAEntryItem from './QAEntryItem/QAEntryItem';
import BusinessInfoEntryItem from './BusinessInfoEntryItem/BusinessInfoEntryItem';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';

const { Text } = Typography;
const { TabPane } = Tabs;

const KnowledgeBaseSection = ({ form, navigate, initialData, onUpdate }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [modalVisible, setModalVisible] = useState(false);
    const [createForm] = Form.useForm();
    const [qaForm] = Form.useForm();
    const [businessForm] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [addingEntry, setAddingEntry] = useState(false);
    const [currentKB, setCurrentKB] = useState(null);
    const [mainTab, setMainTab] = useState('create');
    const [entryTypeTab, setEntryTypeTab] = useState('qa');
    const [isCreatingKB, setIsCreatingKB] = useState(false);
    const [entries, setEntries] = useState({ qa: [], business: [] });

    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNoKnowledgeBases, setHasNoKnowledgeBases] = useState(false);
    const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
    const pageSize = 10;

    const [currentStep, setCurrentStep] = useState(0);
    const [createdKB, setCreatedKB] = useState(null);
    
    // Modify the styles definition at the top to be more specific
    const nonInteractiveStyles = {
        cursor: 'default',
        userSelect: 'none',
        pointerEvents: 'none'
    };
    
    const interactiveControlStyles = {
        cursor: 'pointer',
        pointerEvents: 'auto'
    };
    
    const textStyles = {
        ...nonInteractiveStyles
    };
    
    const textInputStyles = {
        cursor: 'text',
        pointerEvents: 'auto',
        userSelect: 'text'
    };
    
    const buttonStyles = {
        ...interactiveControlStyles
    };

    // Initialize form with initialData if it exists
    useEffect(() => {
        if (initialData) {
            form.setFieldsValue({
                knowledgeBase: {
                    id: initialData.knowledge_base.id,
                    name: initialData.knowledge_base.name
                }
            });

            // Set the selected knowledge base for displaying details
            setSelectedKnowledgeBase(initialData.knowledge_base);

            // Populate options with the initial data
            setOptions([{
                value: initialData.knowledge_base.id.toString(),
                knowledgeBase: initialData.knowledge_base,
                label: (
                    <Flex vertical style={{ padding: '12px' }}>
                        <Flex align="center" style={{ marginBottom: '4px' }}>
                            <DatabaseOutlined style={{ marginInlineEnd: '8px', color: '#1890ff' }} />
                            <Text strong>{initialData.knowledge_base.name}</Text>
                        </Flex>
                        <Text type="secondary" style={{ fontSize: '13px', marginBottom: '4px' }}>
                            {initialData.knowledge_base.description}
                        </Text>
                        <Row gutter={16}>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {t('qa')}: {initialData.knowledge_base.qa_count}
                                </Text>
                            </Col>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {t('info')}: {initialData.knowledge_base.business_info_count}
                                </Text>
                            </Col>
                        </Row>
                    </Flex>
                )
            }]);
        }
    }, [initialData, form, setSelectedKnowledgeBase, setOptions]);

    const fetchKnowledgeBases = useCallback(async (searchTerm = '', page = 1, isInitialLoad = false) => {
        try {
            setLoading(true);
            const response = await getknowledgeDatabases({
                page,
                size: pageSize,
                search: searchTerm
            });

            if (isInitialLoad && response.data.length === 0) {
                setHasNoKnowledgeBases(true);
                return;
            }
            setHasNoKnowledgeBases(false);

            const formattedOptions = response.data.map(kb => ({
                value: kb.id.toString(),
                knowledgeBase: kb,
                label: (
                    <Flex vertical style={{ padding: '12px' }}>
                        <Flex align="center" style={{ marginBottom: '4px' }}>
                            <DatabaseOutlined style={{ marginInlineEnd: '8px', color: '#1890ff' }} />
                            <Text strong>{kb.name}</Text>
                        </Flex>
                        <Text type="secondary" style={{ fontSize: '13px', marginBottom: '4px' }}>
                            {kb.description}
                        </Text>
                        <Row gutter={16}>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {t('qa')}: {kb.qa_count}
                                </Text>
                            </Col>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {t('info')}: {kb.business_info_count}
                                </Text>
                            </Col>
                        </Row>
                    </Flex>
                )
            }));

            setOptions(prevOptions => {
                // Filter out any existing 'load-more' option
                const filteredOptions = page === 1 ? [] : prevOptions.filter(opt => opt.value !== 'load-more');
                
                // Combine previous and new options
                const combinedOptions = [...filteredOptions, ...formattedOptions];
                
                // Add load-more button at the end if there are more results
                if (response.metadata.has_next) {
                    combinedOptions.push({
                        value: 'load-more',
                        disabled: true,
                        label: (
                            <Flex justify="center" style={{ padding: '8px' }}>
                                <Button 
                                    type="link" 
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchKnowledgeBases(form.getFieldValue(['knowledgeBase', 'name']), response.metadata.page + 1);
                                    }}
                                >
                                    {t('load_more')}
                                </Button>
                            </Flex>
                        ),
                    });
                }
                
                return combinedOptions;
            });
            setCurrentPage(response.metadata.page);
        } catch (error) {
            console.error('Error fetching knowledge bases:', error);
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    const debouncedSearch = useMemo(() => debounce((value) => {
        setCurrentPage(1);
        fetchKnowledgeBases(value, 1);
    }, 300), [fetchKnowledgeBases]);

    const handleSearch = (value) => {
        if (!value) {
            // Clear knowledgeBase ID when input is empty
            form.setFieldsValue({
                knowledgeBase: {
                    id: undefined,
                    name: undefined
                }
            });
            
            // Trigger validation after clearing values
            form.validateFields(['knowledgeBase']).catch(() => {
                // This will force the parent component to re-evaluate button state
            });
            
            // Fetch all knowledge bases when input is cleared
            setCurrentPage(1);
            fetchKnowledgeBases('', 1);
            return;
        }
        debouncedSearch(value);
    };

    const handleSelect = (value, option) => {
        form.setFieldsValue({
            knowledgeBase: {
                id: option.knowledgeBase.id,
                name: option.knowledgeBase.name
            }
        });
        setSelectedKnowledgeBase(option.knowledgeBase);
        
        // Notify parent component about the form update if onUpdate exists
        if (onUpdate) {
            onUpdate();
        }
    };

    // Update selectedKnowledgeBase when initialData changes
    useEffect(() => {
        if (initialData?.knowledge_base) {
            setSelectedKnowledgeBase(initialData.knowledge_base);
        }
    }, [initialData]);

    // Update selectedKnowledgeBase when a new knowledge base is created
    useEffect(() => {
        if (createdKB) {
            setSelectedKnowledgeBase(createdKB);
        }
    }, [createdKB]);

    const handleCreateKnowledgeBase = async (values) => {
        try {
            setCreating(true);
            const response = await addKnowledgeDatabase(values);
            const newKB = response.data;
            setCreatedKB(newKB);
            setSelectedKnowledgeBase(newKB);
            
            // Assign the newly created knowledge base to the form field
            form.setFieldsValue({
                knowledgeBase: {
                    id: newKB.id,
                    name: newKB.name
                }
            });
            
            // Validate the field to update form state
            form.validateFields(['knowledgeBase']).then(() => {
                
                // If onUpdate prop is provided, call it to notify parent component
                if (onUpdate) {
                    onUpdate();
                }
            });
            
            setCurrentStep(1);
            message.success(t('knowledge_base_created_successfully'));

        } catch (error) {
            handleError(t('error_creating_knowledge_base'));
        } finally {
            setCreating(false);
        }
    };

    const handleTabChange = (key) => {
        setEntryTypeTab(key);
        // Reset the appropriate form when switching tabs
        if (key === 'qa') {
            qaForm.resetFields();
        } else {
            businessForm.resetFields();
        }
    };

    const handleAddEntry = async (values) => {
        if (!createdKB?.id) {
            message.error(t('knowledge_base_not_created'));
            return;
        }

        try {
            setAddingEntry(true);
            const api = entryTypeTab === 'qa' ? createOrUpdateQAEntry : createOrUpdateBusinessInfoEntry;
            const entryData = entryTypeTab === 'qa'
                ? {
                    knowledge_base_id: createdKB.id,
                    question: values.question,
                    answer: values.answer
                }
                : {
                    knowledge_base_id: createdKB.id,
                    content: values.content
                };

            const response = await api(entryData);
            if (response.success) {
                // Reset the appropriate form based on the active tab
                if (entryTypeTab === 'qa') {
                    qaForm.resetFields();
                } else {
                    businessForm.resetFields();
                }
                message.success(t('entry_created_successfully'));

                // Update entries state with the new entry including its ID
                const newEntry = entryTypeTab === 'qa'
                    ? { 
                        id: response.data.id,
                        question: values.question, 
                        answer: values.answer 
                    }
                    : { 
                        id: response.data.id,
                        content: values.content 
                    };

                setEntries(prev => {
                    const updatedEntries = {
                        ...prev,
                        [entryTypeTab]: [...prev[entryTypeTab], newEntry]
                    };

                    // Update the form with the current entries
                    const currentKnowledgeBase = form.getFieldValue('knowledgeBase');
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...currentKnowledgeBase,
                            entries: updatedEntries
                        }
                    });

                    return updatedEntries;
                });

                // Update the knowledge base counts in the options
                setOptions(prevOptions => {
                    if (!prevOptions || !Array.isArray(prevOptions)) {
                        return [];
                    }
                    return prevOptions.map(option => {
                        if (!option || !option.knowledgeBase || option.knowledgeBase.id !== createdKB.id) {
                            return option;
                        }
                        const updatedKB = { ...option.knowledgeBase };
                        if (entryTypeTab === 'qa') {
                            updatedKB.qa_count = (updatedKB.qa_count || 0) + 1;
                        } else {
                            updatedKB.business_info_count = (updatedKB.business_info_count || 0) + 1;
                        }
                        return {
                            ...option,
                            knowledgeBase: updatedKB,
                            label: (
                                <Flex vertical style={{ padding: '12px' }}>
                                    <Flex align="center" style={{ marginBottom: '4px' }}>
                                        <DatabaseOutlined style={{ marginInlineEnd: '8px', color: '#1890ff' }} />
                                        <Text strong>{updatedKB.name}</Text>
                                    </Flex>
                                    <Text type="secondary" style={{ fontSize: '13px', marginBottom: '4px' }}>
                                        {updatedKB.description}
                                    </Text>
                                    <Row gutter={16}>
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {t('qa')}: {updatedKB.qa_count || 0}
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {t('info')}: {updatedKB.business_info_count || 0}
                                            </Text>
                                        </Col>
                                    </Row>
                                </Flex>
                            )
                        };
                    });
                });
            } else {
                handleError(response.message || t('error_creating_updating_entry'));
            }
        } catch (error) {
            console.error('Error adding entry:', error);
            handleError(t('error_creating_updating_entry'));
        } finally {
            setAddingEntry(false);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        try {
            const { success } = await deleteEntry(entryId);
            if (success) {
                message.success(t('entry_deleted_successfully'));
                
                if (entryTypeTab === 'qa') {
                    setEntries(prev => ({
                        ...prev,
                        qa: prev.qa.filter(entry => entry.id !== entryId)
                    }));
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...form.getFieldValue('knowledgeBase'),
                            entries: {
                                ...form.getFieldValue('knowledgeBase').entries,
                                qa: entries.qa.filter(entry => entry.id !== entryId)
                            }
                        }
                    });
                } else {
                    setEntries(prev => ({
                        ...prev,
                        business: prev.business.filter(entry => entry.id !== entryId)
                    }));
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...form.getFieldValue('knowledgeBase'),
                            entries: {
                                ...form.getFieldValue('knowledgeBase').entries,
                                business: entries.business.filter(entry => entry.id !== entryId)
                            }
                        }
                    });
                }
            } else {
                message.error(t('failed_to_delete_entry'));
            }
        } catch (error) {
            message.error(t('error_deleting_entry'));
        }
    };

    useEffect(() => {
        if (!form.getFieldValue(['knowledgeBase', 'id'])) {
            fetchKnowledgeBases('', 1, true);
        }
    }, [form, fetchKnowledgeBases]);

    if (isCreatingKB) {
        return (
            <Flex vertical style={{
                width: '100%',
                ...nonInteractiveStyles
            }}>
                {currentStep === 0 && <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                        setIsCreatingKB(false);
                        createForm.resetFields();
                        setCurrentKB(null);
                        setCurrentStep(0);
                        setCreatedKB(null);
                        setEntries({ qa: [], business: [] });
                    }}
                    style={{ 
                        padding: 0, 
                        marginBottom: 8, 
                        width: 'fit-content',
                        ...interactiveControlStyles
                    }}
                >
                    {t('back_to_selection')}
                </Button>}

                {currentStep === 0 ? (
                    <Form
                        form={createForm}
                        layout="vertical"
                        onFinish={handleCreateKnowledgeBase}
                        style={{ maxWidth: '100%' }}
                    >
                        <Row gutter={[16, 0]} style={{ margin: 0 }}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="name"
                                    label={t('name')}
                                    rules={[{ required: true, message: t('please_enter_knowledge_base_name') }]}
                                    style={{ marginBottom: 24 }}
                                >
                                    <Input
                                        placeholder={t('enter_knowledge_base_name')}
                                        size="medium"
                                        style={{ ...textInputStyles }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="description"
                                    label={t('description')}
                                    style={{ marginBottom: 32 }}
                                >
                                    <Input.TextArea
                                        placeholder={t('enter_knowledge_base_description')}
                                        rows={1}
                                        size="medium"
                                        style={{ ...textInputStyles }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={creating}
                                        block
                                        size="medium"
                                        style={{ ...interactiveControlStyles }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            createForm.submit();
                                        }}
                                    >
                                        {t('next')}
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                ) : (
                    <Flex vertical>
                        <Flex align="center" gap="middle" style={{ ...nonInteractiveStyles }}>
                            <DatabaseOutlined style={{ fontSize: 24, color: '#1890ff', ...nonInteractiveStyles }} />
                            <Text strong style={{ fontSize: 16, ...nonInteractiveStyles }}>{createdKB?.name}</Text>
                        </Flex>

                        <Tabs
                            activeKey={entryTypeTab}
                            onChange={handleTabChange}
                            style={{ marginBottom: 24 }}
                            tabBarStyle={{ marginBottom: 24 }}
                        >
                            <TabPane tab={t('qa_entries')} key="qa">
                                <Row gutter={[24, 0]} style={{ margin: 0 }}>
                                    <Col xs={24} md={12}>
                                        <Form
                                            form={qaForm}
                                            layout="vertical"
                                            onFinish={handleAddEntry}
                                            style={{ maxWidth: '100%' }}
                                        >
                                            <Form.Item
                                                name="question"
                                                label={t('question')}
                                                rules={[{ required: true, message: t('please_enter_question') }]}
                                                style={{ marginBottom: 24 }}
                                            >
                                                <Input.TextArea
                                                    rows={2}
                                                    placeholder={t('enter_question_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                name="answer"
                                                label={t('answer')}
                                                rules={[{ required: true, message: t('please_enter_answer') }]}
                                                style={{ marginBottom: 32 }}
                                            >
                                                <Input.TextArea
                                                    rows={4}
                                                    placeholder={t('enter_answer_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item style={{ marginBottom: 0 }}>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={addingEntry}
                                                    block
                                                    size="medium"
                                                    style={{ ...interactiveControlStyles }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        qaForm.validateFields().then(values => {
                                                            handleAddEntry(values);
                                                        });
                                                    }}
                                                >
                                                    {t('add_qa_entry')}
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        {entries.qa.length > 0 ? (
                                            <div>
                                                <Flex vertical gap="middle">
                                                    {entries.qa.map((entry) => (
                                                        <QAEntryItem
                                                            key={entry.id}
                                                            entry={entry}
                                                            onDelete={handleDeleteEntry}
                                                        />
                                                    ))}
                                                </Flex>
                                            </div>
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={t('no_qa_entries_yet')}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tab={t('business_information')} key="business">
                                <Row gutter={[24, 0]} style={{ margin: 0 }}>
                                    <Col xs={24} md={12}>
                                        <Form
                                            form={businessForm}
                                            layout="vertical"
                                            onFinish={handleAddEntry}
                                            style={{ maxWidth: '100%' }}
                                        >
                                            <Form.Item
                                                name="content"
                                                label={t('business_information')}
                                                rules={[{ required: true, message: t('please_enter_business_information') }]}
                                                style={{ marginBottom: 32 }}
                                            >
                                                <Input.TextArea
                                                    rows={6}
                                                    placeholder={t('enter_business_information_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={addingEntry}
                                                    block
                                                    size="medium"
                                                    style={{ ...interactiveControlStyles }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        businessForm.validateFields().then(values => {
                                                            handleAddEntry(values);
                                                        });
                                                    }}
                                                >
                                                    {t('add_business_information')}
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        {entries.business.length > 0 ? (
                                            <div>
                                                <Flex vertical gap="middle">
                                                    {entries.business.map((entry) => (
                                                        <BusinessInfoEntryItem
                                                            key={entry.id}
                                                            entry={entry}
                                                            onDelete={handleDeleteEntry}
                                                        />
                                                    ))}
                                                </Flex>
                                            </div>
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={t('no_business_information_yet')}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </TabPane>
                        </Tabs>
                    </Flex>
                )}
            </Flex>
        );
    }

    if (hasNoKnowledgeBases && !form.getFieldValue(['knowledgeBase', 'id'])) {
        return (
            <Col xs={24} md={12}>
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: '0', width: '100%', paddingBottom: '3px', ...nonInteractiveStyles }}
                    description={
                        <Space direction='vertical' style={{ width: '100%', ...nonInteractiveStyles }}>
                            <Text type="secondary" style={{ width: '100%', ...nonInteractiveStyles }} >{t('no_knowledge_bases_found')}</Text>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsCreatingKB(true)}
                                style={{ width: '100%', ...buttonStyles }}
                            >
                                {t('create_first_knowledge_base')}
                            </Button>
                        </Space>
                    }
                />
            </Col>
        );
    }

    return (
        <Col xs={24} md={12}>
            <Form.Item
                label={t('knowledge_base')}
                required
                style={{ marginBottom: 4 }}
            >
                <Form.Item
                    name={['knowledgeBase', 'name']}
                    rules={[{ required: true, message: t('please_select_a_knowledge_base') }]}
                    style={{ marginBottom: 0 }}
                >
                    {form.getFieldValue(['knowledgeBase', 'id']) ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%' }}>
                            <div 
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '8px 12px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    border: '1px solid #d9d9d9',
                                    gap: '4px',
                                    width: '100%',
                                    ...nonInteractiveStyles
                                }}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    marginBottom: '4px',
                                    ...nonInteractiveStyles
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        ...nonInteractiveStyles
                                    }}>
                                        <DatabaseOutlined style={{ 
                                            color: '#1890ff', 
                                            fontSize: '16px',
                                            ...nonInteractiveStyles
                                        }} />
                                        <Text strong style={{ ...nonInteractiveStyles }}>{form.getFieldValue(['knowledgeBase', 'name'])}</Text>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <Button
                                            type="link"
                                            size="small"
                                            style={{ 
                                                padding: '0 4px',
                                                ...interactiveControlStyles
                                            }}
                                            onClick={() => {
                                                form.setFieldsValue({
                                                    knowledgeBase: {
                                                        id: undefined,
                                                        name: undefined
                                                    }
                                                });
                                                setSelectedKnowledgeBase(null);
                                                form.validateFields(['knowledgeBase']).catch(() => {
                                                    // Required for parent validation
                                                });
                                                fetchKnowledgeBases('', 1);
                                                // Notify parent component about the form update if onUpdate exists
                                                if (onUpdate) {
                                                    onUpdate();
                                                }
                                            }}
                                        >
                                            {t('change')}
                                        </Button>
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                form.setFieldsValue({
                                                    knowledgeBase: {
                                                        id: undefined,
                                                        name: undefined
                                                    }
                                                });
                                                setSelectedKnowledgeBase(null);
                                                form.validateFields(['knowledgeBase']).catch(() => {
                                                    // Required for parent validation
                                                });
                                                fetchKnowledgeBases('', 1);
                                                // Notify parent component about the form update if onUpdate exists
                                                if (onUpdate) {
                                                    onUpdate();
                                                }
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '0 4px',
                                                ...interactiveControlStyles
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Knowledge base details */}
                                {selectedKnowledgeBase && (
                                    <>
                                        <Text type="secondary" style={{ 
                                            fontSize: '13px', 
                                            marginBottom: '4px',
                                            ...nonInteractiveStyles
                                        }}>
                                            {selectedKnowledgeBase.description || ''}
                                        </Text>
                                        <Row gutter={16} style={{ ...nonInteractiveStyles }}>
                                            <Col style={{ ...nonInteractiveStyles }}>
                                                <Text type="secondary" style={{ 
                                                    fontSize: '12px',
                                                    ...nonInteractiveStyles
                                                }}>
                                                    {t('qa')}: {selectedKnowledgeBase.qa_count || 0}
                                                </Text>
                                            </Col>
                                            <Col style={{ ...nonInteractiveStyles }}>
                                                <Text type="secondary" style={{ 
                                                    fontSize: '12px',
                                                    ...nonInteractiveStyles
                                                }}>
                                                    {t('info')}: {selectedKnowledgeBase.business_info_count || 0}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <AutoComplete
                            options={options}
                            onSearch={handleSearch}
                            onSelect={handleSelect}
                            allowClear
                            onClear={() => {
                                form.setFieldsValue({
                                    knowledgeBase: {
                                        id: undefined,
                                        name: undefined
                                    }
                                });
                                // Trigger validation after clearing
                                form.validateFields(['knowledgeBase']).catch(() => {
                                    // Required for parent validation
                                });
                                // Fetch all knowledge bases again
                                fetchKnowledgeBases('', 1);
                                // Notify parent component about the form update if onUpdate exists
                                if (onUpdate) {
                                    onUpdate();
                                }
                            }}
                            placeholder={t('search_knowledge_base')}
                            style={{ width: '100%', ...textInputStyles }}
                            notFoundContent={loading ? <Spin size="small" /> : t('no_matching_knowledge_base_found')}
                            prefix={loading ? <Spin size="small" style={{ color: '#bfbfbf' }} /> : <SearchOutlined style={{ color: '#bfbfbf' }} />}
                            dropdownRender={(menu) => (
                                <Flex vertical>
                                    {menu}
                                    {loading && (
                                        <Flex justify="center" style={{ padding: '12px' }}>
                                            <Spin size="small" />
                                        </Flex>
                                    )}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Flex style={{ padding: '8px 12px' }}>
                                        <Button
                                            type="link"
                                            icon={<PlusOutlined />}
                                            onClick={() => setIsCreatingKB(true)}
                                            className="create-kb-button"
                                        >
                                            {t('create_new_knowledge_base')}
                                        </Button>
                                    </Flex>
                                </Flex>
                            )}
                        />
                    )}
                </Form.Item>
                <Form.Item
                    name={['knowledgeBase', 'id']}
                    style={{ display: 'none' }}
                >
                    <input />
                </Form.Item>
            </Form.Item>
        </Col>
    );
};

export default KnowledgeBaseSection;
